// Página "Mi Trámite" — estado del expediente + documentación del cliente
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { ClienteTramiteHeader } from '@/components/clientes/ClienteTramiteHeader';
import { ClienteChecklistProgress } from '@/components/clientes/ClienteChecklistProgress';
import { ClienteDocumentList } from '@/components/clientes/ClienteDocumentList';
import { ClienteDocumentUpload } from '@/components/clientes/ClienteDocumentUpload';
import { ClienteSolicitudesBanner } from '@/components/clientes/ClienteSolicitudesBanner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InboxIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SUPPORT_EMAIL } from '@/config/constants';
import {
  useClienteFichaQuery,
  useClienteDocuments,
  useUploadDocument,
  useDeleteDocument,
} from '@/hooks/useClienteExpediente';
import { useAuth } from '@/providers/AuthProvider';
import { hasCompletedClientOnboarding } from '@/pages/ClienteOnboarding';

export function ClientesTramite() {
  const { t } = useTranslation('portal');
  const { user } = useAuth();
  const { data: ficha, isLoading, error, refetch } = useClienteFichaQuery();

  // Redirect new clients to onboarding if not completed
  const workspace = ficha?.expediente_workspace;
  const expedienteId = workspace?.expediente_id;

  // Hooks must run unconditionally (rules-of-hooks); these queries are
  // undefined-safe, so the onboarding redirect is evaluated after them.
  const { data: documents = [] } = useClienteDocuments(expedienteId);
  const uploadMutation = useUploadDocument(expedienteId);
  const deleteMutation = useDeleteDocument(expedienteId);

  const [uploadSuccess, setUploadSuccess] = useState(false);

  if (user?.role === 'client' && !hasCompletedClientOnboarding()) {
    return <Navigate to="/clientes/onboarding" replace />;
  }

  const handleUpload = (file: File, tipoDocumento?: string) => {
    setUploadSuccess(false);
    uploadMutation.mutate(
      { file, tipoDocumento },
      {
        onSuccess: () => {
          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 3000);
        },
      }
    );
  };

  const handleDelete = (fileId: string) => {
    deleteMutation.mutate(fileId);
  };

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">{t('process.loadError')}</p>
            <Button variant="outline" onClick={() => refetch()}>
              {t('common.retry', 'Reintentar')}
            </Button>
          </CardContent>
        </Card>
      </ClientLayout>
    );
  }

  // Estado vacío: sin expediente. Con contrato activo el copy cambia
  // ("estamos preparando tu expediente") — mismo componente visual.
  if (!workspace) {
    const hasContract =
      (ficha?.contract_mandates?.length ?? 0) > 0 || ficha?.contract_mandate != null;
    return (
      <ClientLayout>
        <div className="text-center py-16">
          <InboxIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {hasContract ? t('process.notStartedWithContractTitle') : t('process.notStartedTitle')}
          </h2>
          <p className="text-gray-500 max-w-md mx-auto">
            {hasContract ? t('process.notStartedWithContractDesc') : t('process.notStartedDesc')}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('process.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('process.subtitle')}
          </p>
        </div>

        {/* Banner solicitudes pendientes */}
        <ClienteSolicitudesBanner />

        {/* Estado y progreso */}
        <ClienteTramiteHeader workspace={workspace} />

        {/* Checklist de faltantes */}
        <ClienteChecklistProgress
          faltantes={workspace.faltantes_priorizados ?? []}
        />

        {/* Documentación */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('process.documentation')}
          </h3>

          {/* Upload */}
          <ClienteDocumentUpload
            onUpload={handleUpload}
            isUploading={uploadMutation.isPending}
          />

          {/* Mensaje de éxito */}
          {uploadSuccess && (
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('process.uploadSuccess')}
              </p>
            </div>
          )}

          {/* Error de upload */}
          {uploadMutation.isError && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-300">
                {t('process.uploadError')}
              </p>
            </div>
          )}

          {/* Lista de documentos */}
          <ClienteDocumentList
            documents={documents}
            onDelete={handleDelete}
            isDeleting={deleteMutation.isPending}
          />
        </div>
      </div>
    </ClientLayout>
  );
}
